import React from 'react';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Divider } from '@mui/material';

import { hasTrussDesignPermission } from '@paragon/login-tools';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { useAppMonitor } from '../../../../monitor';
import { lockIndividualPlatePairs } from '../../../../store/actions/lockOptionsActions';
import { getActiveComponentDesign } from '../../../../store/selectors/getActiveComponentDesignSelector';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { EditPlatePairMenuReplacement } from '../../menu-replacements/EditPlatePairMenuReplacement';
import { useSceneMenuContext } from '../common/context';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuFragment } from '../common/SceneMenuFragment';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function PlatePairMenu(props: { guid: string; matchingSelectedItems: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();
  const menuContext = useSceneMenuContext();

  const canEditTrusses = useAppSelector(hasTrussDesignPermission);
  const componentDesign = useAppSelector(getActiveComponentDesign);
  if (componentDesign == null || !canEditTrusses) return null;

  const platePairGuids = new Set(
    props.matchingSelectedItems
      .filter((item) => item.type === 'platePair')
      .map((item) => item.guid)
      .concat(props.guid),
  );

  const platePairs = componentDesign.component.platePairs.filter((platePair) => platePairGuids.has(platePair.guid));
  if (platePairs.length === 0) return null;

  const allLocked =
    componentDesign.platingLocked ||
    Array.from(platePairGuids).every((guid) => componentDesign.lockedPlatePairs.includes(guid));

  const userVisibleTermWithQuantity = platePairs.length > 1 ? `${platePairs.length} Plate Pairs` : 'Plate Pair';

  return (
    <SceneMenuFragment>
      <EditPlatePairMenuReplacement platePairs={platePairs} />

      <SceneMenuContent>
        <>
          <SceneMenuItem
            text="Edit Properties"
            icon={<EditIcon />}
            onClick={async (event) => {
              event.preventDefault();
              menuContext.activateReplacement('edit');

              monitor.recordInteraction('Plate Pair => Edit Properties menu item clicked');
            }}
          />

          <Divider />
        </>

        <SceneMenuItem
          text={`${allLocked ? 'Unlock' : 'Lock'} ${userVisibleTermWithQuantity}`}
          icon={allLocked ? <LockOpenIcon /> : <LockIcon />}
          onClick={() => {
            dispatch(lockIndividualPlatePairs(!allLocked, ...platePairGuids));
            monitor.recordInteraction('Plate Pair => Lock/Unlock menu item clicked');
          }}
        />

        <DeleteMenuItem
          type="platePair"
          item={platePairs}
          userVisibleTerm={userVisibleTermWithQuantity}
          monitorText="Plate Pair => Delete Plate Pairs menu item clicked"
        />
      </SceneMenuContent>
    </SceneMenuFragment>
  );
}
